/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Text,
    Radio,
    Stack,
    RadioGroup
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const RadioRefinement = ({filter, value, toggleFilter, isSelected}) => {
    const buttonRef = useRef()
    const {formatMessage} = useIntl()
    // Because choosing a refinement is equivalent to a form submission, the best semantic choice
    // for the refinement is a button or a link, rather than a radio input. The radio element here
    // is purely for visual purposes, and should probably be replaced with a simple icon.
    return (
        <Box display="flex">
            <Radio
                display="inline-flex"
                height={{base: '44px', lg: '24px'}}
                isChecked={isSelected}
                alignItems="center"
                // Ideally, this "icon" would be part of the button, but doing so with a radio input
                // triggers `onClick` twice. The radio must be separate, and therefore we must add
                // these workarounds to prevent it from receiving focus.
                inputProps={{'aria-hidden': true, tabIndex: -1}}
                onClick={() => {
                    buttonRef.current?.click()
                }}
            ></Radio>
            <Text
                ref={buttonRef}
                ml={2}
                className="radio-button"
                as="button"
                fontSize="16px"
                marginLeft="10px"
                color="black"
                onClick={() => toggleFilter(value, filter.attributeId, isSelected, false)}
                aria-label={formatMessage(isSelected ? REMOVE_FILTER : ADD_FILTER, value)}
            >
                {value.label}
            </Text>
        </Box>
    )
}

RadioRefinement.propTypes = {
    filter: PropTypes.object,
    value: PropTypes.object,
    toggleFilter: PropTypes.func,
    isSelected: PropTypes.bool
}

const RadioRefinements = ({filter, toggleFilter, selectedFilters, isMenu}) => {
    // Add this to track the selected value
    const [value, setValue] = React.useState('')
    return (
        <Stack
            maxHeight="300px"
            overflowY="auto"
            sx={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                '&::-webkit-scrollbar': {
                    width: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'gray.800',
                    borderRadius: '2px',
                    width: '4px',
                    display: 'block'
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'gray.400',
                    borderRadius: '2px',
                    width: '4px',
                    display: 'block'
                },
                userSelect: 'none',
                scrollBehavior: 'smooth'
            }}
        >
            <RadioGroup
                value={value}
                onChange={setValue}
                gap={{base: isMenu ? '10px' : '20px', lg: '20px'}}
                display="flex"
                flexDirection="column"
                margin={{
                    base: isMenu ? '10px 10px 0px 0' : '10px 10px 10px 0',
                    lg: '10px 10px 10px 0'
                }}
            >
                {filter.values.map((value) => {
                    const isSelected = selectedFilters.includes(value.value)
                    // Don't display refinements with no results, unless we got there by selecting too
                    // many refinements
                    if (value.hitCount === 0 && !isSelected) return
                    return (
                        <RadioRefinement
                            key={value.value}
                            value={value}
                            filter={filter}
                            toggleFilter={toggleFilter}
                            isSelected={isSelected}
                        />
                    )
                })}
            </RadioGroup>
        </Stack>
    )
}

RadioRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.arrayOf(PropTypes.object),
    isMenu: PropTypes.bool
}

export default React.memo(RadioRefinements)
